<template>
    <section
        ref="sectionRef"
        class="slice slice-hero slice-hero--product"
        :class="{'slice-hero--product--animate': animate}"
        aria-label="hero"
        data-testid="slice-hero-product"
    >
        <div class="slice-hero--product__background">
            <MaltImg src="/cms-front/ai-header.svg?vh=b817ac" class="slice-hero--product__background-svg" alt="" width="2000" />
        </div>
        <PrismicImage
            v-if="slice.primary.logo?.url"
            :field="slice.primary.logo"
            class="slice-hero--product__logo"
            data-testid="slice-hero-product-logo"
        />
        <div class="slice-content">
            <h1 class="slice-hero--product__title" data-testid="slice-hero-product-title">
                <span v-if="titleLines[0]" class="slice-hero--product__slide-in">
                    <span>{{ titleLines[0] }}</span>
                </span>
                <span v-if="titleLines[1]" class="slice-hero--product__slide-in">
                    <span>{{ titleLines[1] }}</span>
                </span>
                <strong v-if="gradientText" class="slice-hero--product__slide-in">
                    <span>
                        {{ gradientText }}
                        <MktHeroProductSparkle class="slice-hero--product__sparkle" :animate="sparkleAnimate" />
                    </span>
                </strong>
            </h1>
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.description)"
                :field="slice.primary.description"
                class="slice-hero--product__description slice-hero--product__slide-in"
                data-testid="slice-hero-product-description"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice, HeroSliceHeroProduct} from '@prismicio-types';
    import {MaltImg, MktHeroProductSparkle} from '#components';

    const props = defineProps(getSliceComponentProps<HeroSlice>());
    const slice = computed(() => props.slice as HeroSliceHeroProduct);

    const sectionRef = ref<HTMLElement>();
    const animate = ref(false);
    const sparkleAnimate = ref(false);

    const titleLines = computed(() => {
        const text = isFilled.richText(slice.value.primary.title) ? asText(slice.value.primary.title) : '';
        return text.split('\n', 2).map((line) => line.trim());
    });

    const gradientText = computed(() => (isFilled.keyText(slice.value.primary.gradientText) ? slice.value.primary.gradientText : ''));

    function playAnimation() {
        const baseDelay = 0.5;
        const staggerDelay = 0.125;

        const items = sectionRef.value?.querySelectorAll('.slice-hero--product__slide-in');
        items?.forEach((el, i) => {
            const child = el.firstElementChild as HTMLElement | null;
            if (child) {
                child.style.transitionDelay = `${baseDelay + i * staggerDelay}s`;
            }
        });
        animate.value = true;

        setTimeout(() => {
            sparkleAnimate.value = true;
        }, 1000);
    }

    onMounted(() => {
        playAnimation();
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/_text.scss' as fonts;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $ref-width: 1920;

    $sparkle-x: calc(10 / $ref-width) * 100vw;
    $sparkle-width-value: 70;
    $sparkle-width-max: $sparkle-width-value * 1px;
    $sparkle-width: calc($sparkle-width-value / $ref-width) * 100vw;

    $title-y-value: 313;
    $title-y: calc($title-y-value / $ref-width) * 100vw;

    $title-font-size-value: 130;
    $title-font-size-max: $title-font-size-value * 1px;
    $title-font-size: calc($title-font-size-value / $ref-width) * 100vw;

    $subtitle-font-size-value: 100;
    $subtitle-font-size-max: $subtitle-font-size-value * 1px;
    $subtitle-font-size: calc($subtitle-font-size-value / $ref-width) * 100vw;

    $logo-width-value: 153.1 * 2;
    $logo-width-max: $logo-width-value * 1px;
    $logo-width: calc($logo-width-value / $ref-width) * 100vw;

    .slice-hero {
        &--product {
            --ai-transition-duration: 0.7s;
            --ai-transition-duration-long: 1s;
            --ai-transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

            text-align: center;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            padding-top: clamp(110px, $title-y, $title-y);

            &__background {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                overflow: hidden;
                pointer-events: none;
            }

            &__background-svg {
                width: 100%;
                height: auto;
                transform: translate3d(0, -50%, 0) scaleX(2);
                transition: transform 1.5s var(--ai-transition-timing-function);
            }

            &--animate &__background-svg {
                transform: translate3d(0, 0, 0) scaleX(1);
            }

            &__logo {
                position: absolute;
                right: 2.5vw;
                top: 5vw;
                width: clamp(30px, $logo-width, $logo-width-max);
                height: auto;
                pointer-events: none;
                filter: brightness(0) saturate(100%) invert(100%);

                img {
                    display: block;
                    width: 100%;
                    height: auto;
                }
            }

            .slice-content {
                position: relative;
                text-align: center;
                padding-top: 0;
                padding-bottom: calc(48px - 40px);

                @include mq.screen_lg(min) {
                    padding-bottom: calc(75px - 64px);
                }
            }

            &__title {
                font-family: var(--joy-font-family-title);
                font-size: clamp(36px, $title-font-size, $title-font-size-max);
                color: var(--joy-color-text-heading);
                text-align: center;
                margin-bottom: 0;
                user-select: none;

                > span {
                    line-height: var(--joy-line-height-small);

                    > span {
                        display: inline-block;
                    }
                }

                strong {
                    line-height: var(--joy-line-height-small);
                    font-family: var(--joy-font-family-title);
                    font-size: clamp(26px, $subtitle-font-size, $subtitle-font-size-max);

                    > span {
                        display: inline-block;
                        background-image: var(--joy-color-ai-gradient-30);
                        background-clip: text;
                        color: transparent;
                    }
                }
            }

            &__description {
                display: inline-flex;
                @include fonts.getFontBySize(primary-300);
                text-align: center;
                margin: 48px auto 0;
                max-width: 550px;

                @include mq.screen_sm(min) {
                    @include fonts.getFontBySize(primary-800);
                    max-width: 940px;
                    padding: 0 60px;
                }

                @include mq.screen_md(min) {
                    max-width: 1080px;
                    padding: 0 100px;
                    margin-top: 75px;
                    font-size: var(--joy-font-size-secondary-200);
                    line-height: var(--joy-line-height-large);
                }

                @include mq.screen_xlg(min) {
                    font-size: var(--joy-font-size-secondary-400);
                    padding: 0 70px;
                }
            }

            &__sparkle {
                position: absolute;
                bottom: 0;
                margin-left: clamp(4px, $sparkle-x, 10px);
                width: clamp(10px, $sparkle-width, $sparkle-width-max);
                height: auto;
            }

            &__slide-in {
                position: relative;
                display: block;
                overflow: hidden;

                > * {
                    transform: translate3d(0, 101%, 0);
                    will-change: transform;
                }
            }

            &--animate &__slide-in > * {
                transition: transform var(--ai-transition-duration-long) var(--ai-transition-timing-function) 0.3s;
                transform: translate3d(0, 0, 0);
            }
        }
    }
</style>
